import { inject } from '@angular/core';
import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { catchError, from, switchMap, throwError } from 'rxjs';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // Don't intercept Firebase token refresh calls — they don't use our auth headers
  if (req.url.includes('securetoken.googleapis.com')) {
    return next(req);
  }

  return next(req).pipe(
    catchError((error) => {
      // This device was signed out from "Where you're logged in" (or the account was
      // suspended). Refreshing won't help — the session itself is gone — so sign out now.
      if (error instanceof HttpErrorResponse && error.status === 401 && error.error?.error === 'Session Revoked') {
        authService.clearTokens();
        void authService.signOut().finally(() => router.navigate(['/login']));
        return throwError(() => error);
      }
      // On first 401, attempt a silent token refresh and retry — but only if a
      // user session exists. Without a session there is nothing to refresh, and
      // retrying would mask expected 401s (e.g. wrong password on login).
      if (
        error instanceof HttpErrorResponse &&
        error.status === 401 &&
        !req.headers.has('X-Auth-Retried') &&
        authService.currentUser() != null
      ) {
        return from(authService.getIdTokenAsync()).pipe(
          switchMap((newToken) => {
            if (newToken) {
              const retried = req.clone({
                setHeaders: {
                  Authorization: `Bearer ${newToken}`,
                  'X-Auth-Retried': '1',
                },
              });
              return next(retried);
            }
            // No token after refresh — session is gone; redirect to login
            authService.clearTokens();
            router.navigate(['/login']);
            return throwError(() => error);
          }),
        );
      }
      return throwError(() => error);
    }),
  );
};
